/**
 * Utilities for handling item-select-config from Grouse
 * Types are inferred from GraphQL query results to avoid duplication
 */

import type { OrderItem } from '../graphql/query/getOrder';
import type {
  ItemSelectConfig,
  ItemSelectProperty,
} from '../graphql/query/getRetailerSettings';

export const DEFAULT_ITEM_SELECT_CONFIG: ItemSelectConfig = {
  item_properties: [
    { property: 'ITEM_NAME', enabled: true },
    { property: 'PRICE_PAID', enabled: true },
    { property: 'QTY', enabled: true },
    { property: 'SIZE', enabled: true },
    { property: 'COLOR', enabled: true },
    { property: 'PRODUCT_CODE', enabled: false },
    { property: 'BRAND', enabled: false },
    { property: 'CATEGORY', enabled: false },
    { property: 'STYLE', enabled: false },
  ],
  item_order: {
    order_by: 'ITEM_NAME',
    direction: 'ASC',
  },
};

/**
 * Maps Grouse property names to PDXOrderItem field paths
 * Note: quantity is an object with { ordered, pending, completed, remaining }
 * so we use quantity.remaining for sorting
 */
const PROPERTY_TO_FIELD_MAP: Record<ItemSelectProperty, string> = {
  ITEM_NAME: 'name',
  PRICE_PAID: 'unit_price',
  QTY: 'quantity.remaining',
  SIZE: 'size',
  COLOR: 'color',
  PRODUCT_CODE: 'sku',
  BRAND: 'attributes.brand',
  CATEGORY: 'attributes.category',
  STYLE: 'attributes.style',
};

/**
 * Safe default field to use if order_by is invalid
 */
const SAFE_DEFAULT_FIELD: ItemSelectProperty = 'ITEM_NAME';

/**
 * Type guard to check if a value is a valid ItemSelectProperty
 */
function isValidProperty(x: unknown): x is ItemSelectProperty {
  return typeof x === 'string' && x in PROPERTY_TO_FIELD_MAP;
}

/**
 * Coerce a value to a number, returning null if not a valid number
 * Handles currency strings like "$12.99", "CAD 15.00", etc.
 */
function coerceNumber(x: unknown): number | null {
  if (typeof x === 'number') return x;
  if (typeof x === 'string') {
    // Remove currency symbols, letters, and whitespace, keeping only digits, dots, and minus
    const cleaned = x.replace(/[^0-9.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/**
 * Get value from object by path (supports nested paths like 'attributes.brand')
 * Uses hasOwnProperty to avoid prototype chain traversal
 */
function getValueByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce((acc, part) => {
    if (
      acc &&
      typeof acc === 'object' &&
      Object.prototype.hasOwnProperty.call(acc, part)
    ) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

/**
 * Get the field name to sort by based on config
 * Falls back to ITEM_NAME if order_by is invalid
 */
export function getSortField(config: ItemSelectConfig): string {
  const key = config?.item_order?.order_by;
  const safeKey = isValidProperty(key) ? key : SAFE_DEFAULT_FIELD;
  return PROPERTY_TO_FIELD_MAP[safeKey];
}

/**
 * Maps ItemSelectProperty to the corresponding field name in variant.info
 * Used to filter which attributes are displayed on item cards
 */
const PROPERTY_TO_DISPLAY_NAME: Record<ItemSelectProperty, string> = {
  ITEM_NAME: 'name', // Not shown in variant.info (it's the item name itself)
  PRICE_PAID: 'price',
  QTY: 'quantity',
  SIZE: 'size',
  COLOR: 'color',
  PRODUCT_CODE: 'sku',
  BRAND: 'brand',
  CATEGORY: 'category',
  STYLE: 'style',
};

export interface ItemDetail {
  name: string;
  value: string;
}

/**
 * Filter item details based on item-select-config from Grouse
 * Only shows attributes that are enabled in the config
 *
 * @param details - Array of item details from variant.info
 * @param config - ItemSelectConfig from Grouse
 * @returns Filtered array of details to display
 */
export function filterItemDetailsByConfig(
  details: ItemDetail[] | undefined,
  config: ItemSelectConfig | null | undefined,
): ItemDetail[] {
  if (!details || details.length === 0) {
    return [];
  }

  // If no config provided, show all details
  if (!config) {
    return details;
  }

  // Get enabled properties from config
  const enabledProperties = new Set(
    config.item_properties
      .filter((prop) => prop.enabled)
      .map((prop) => PROPERTY_TO_DISPLAY_NAME[prop.property]?.toLowerCase())
      .filter(Boolean),
  );

  // If no properties are enabled, show nothing
  if (enabledProperties.size === 0) {
    return [];
  }

  // Filter details to only show enabled properties
  return details.filter((detail) => {
    const detailName = detail.name?.toLowerCase();
    return detailName && enabledProperties.has(detailName);
  });
}

/**
 * Check if price should be displayed based on item-select-config
 *
 * @param config - ItemSelectConfig from Grouse
 * @returns true if PRICE_PAID is enabled in the config
 */
export function shouldShowPrice(
  config: ItemSelectConfig | null | undefined,
): boolean {
  if (!config) {
    return true; // Default: show price
  }

  const priceProp = config.item_properties.find(
    (prop) => prop.property === 'PRICE_PAID',
  );

  return priceProp?.enabled ?? false;
}

/**
 * Get display configuration for item details and price
 * Combines filtering and price visibility logic into a single function
 *
 * @param itemInfo - Object with price and details from separatePriceFromAttributes
 * @param config - ItemSelectConfig from Grouse
 * @returns Filtered details and whether to show price
 */
export function getItemDisplayConfig(
  itemInfo: { price: string | null; details: ItemDetail[] } | undefined,
  config: ItemSelectConfig | null | undefined,
): { details: ItemDetail[]; showPrice: boolean } {
  if (!itemInfo) {
    return { details: [], showPrice: false };
  }

  return {
    details: filterItemDetailsByConfig(itemInfo.details, config),
    showPrice: shouldShowPrice(config) && !!itemInfo.price,
  };
}

/**
 * Sort items based on item-select-config
 *
 * **Null Ordering Semantics:**
 * Items with null/undefined values for the sort field are **always placed last**,
 * regardless of sort direction (ASC or DESC). This ensures:
 * - Predictable, consistent ordering
 * - Items with missing data don't interfere with the primary sort
 * - Better UX - complete items appear first
 *
 * **Examples:**
 * - Sorting by price ASC: [$5, $10, $20, null, null]
 * - Sorting by price DESC: [$20, $10, $5, null, null]
 * - Sorting by name ASC: [Apple, Banana, Cherry, null]
 * - Sorting by name DESC: [Cherry, Banana, Apple, null]
 */
export function sortItemsByConfig(
  items: OrderItem[],
  config: ItemSelectConfig,
): OrderItem[] {
  const sortField = getSortField(config);
  // Validate direction, default to ASC if invalid
  const direction = config?.item_order?.direction === 'DESC' ? 'DESC' : 'ASC';

  return [...items].sort((a, b) => {
    const aValue = getValueByPath(a, sortField);
    const bValue = getValueByPath(b, sortField);

    // Handle null/undefined - always sort nulls last regardless of direction
    // This ensures items with missing data appear at the end of the list
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1; // a is null, sort after b
    if (bValue == null) return -1; // b is null, sort after a

    // Try to coerce to numbers for numeric fields (price, quantity)
    const aNum = coerceNumber(aValue);
    const bNum = coerceNumber(bValue);
    if (aNum !== null && bNum !== null) {
      return direction === 'ASC' ? aNum - bNum : bNum - aNum;
    }

    // Default to string comparison
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();

    if (aStr < bStr) return direction === 'ASC' ? -1 : 1;
    if (aStr > bStr) return direction === 'ASC' ? 1 : -1;

    // Tie-breaker for stability: use id or sku to ensure consistent ordering
    const aId = String(a.id ?? a.sku ?? '');
    const bId = String(b.id ?? b.sku ?? '');
    if (aId < bId) return -1;
    if (aId > bId) return 1;
    return 0;
  });
}
